// analytics.js - Firebase Analytics wrapper module
// All custom event tracking goes through this module.
// No other module should call Firebase Analytics directly.

import { getApp } from "https://www.gstatic.com/firebasejs/10.12.2/firebase-app.js";
import { getAnalytics, logEvent, setUserProperties } from "https://www.gstatic.com/firebasejs/10.12.2/firebase-analytics.js";
import { getRemoteConfig, fetchAndActivate, getValue } from "https://www.gstatic.com/firebasejs/10.12.2/firebase-remote-config.js";

let analytics = null;
let initFailed = false;
let remoteConfig = null;
let remoteConfigInitFailed = false;

function ensureInitialized() {
    if (analytics) return true;
    if (initFailed) return false;

    try {
        const app = getApp();
        analytics = getAnalytics(app);
        return true;
    } catch (error) {
        initFailed = true;
        console.warn("Analytics unavailable (ad blocker or initialization error):", error.message);
        return false;
    }
}

/**
 * Track game start event.
 * @param {{ game_mode: string, ai_difficulty: string|null, special_cards_enabled: boolean }} params
 */
export function trackGameStart(params) {
    if (!ensureInitialized()) return;
    logEvent(analytics, 'game_start', params);
}

/**
 * Track game end event.
 * @param {{ result: string, score: number, rounds: number, duration: number }} params
 */
export function trackGameEnd(params) {
    if (!ensureInitialized()) return;
    logEvent(analytics, 'game_end', params);
}

/**
 * Track action performed event.
 * @param {string} actionType - "sow", "harvest", or "stockpile"
 */
export function trackAction(actionType) {
    if (!ensureInitialized()) return;
    logEvent(analytics, 'action_performed', { action_type: actionType });
}

/**
 * Track special card played event.
 * @param {{ card_id: string, game_mode: string }} params
 */
export function trackSpecialCardPlayed(params) {
    if (!ensureInitialized()) return;
    logEvent(analytics, 'special_card_played', params);
}

/**
 * Track level up event.
 * @param {{ new_level: number, phase: string }} params
 */
export function trackLevelUp(params) {
    if (!ensureInitialized()) return;
    logEvent(analytics, 'level_up', params);
}

/**
 * Track theme unlock event.
 * @param {{ theme_name: string, level: number }} params
 */
export function trackThemeUnlock(params) {
    if (!ensureInitialized()) return;
    logEvent(analytics, 'theme_unlock', params);
}

/**
 * Set user properties for analytics segmentation.
 * Properties persist on the session and attach to all subsequent events.
 * Values are converted to strings per Firebase Analytics requirements.
 * @param {{ current_level?: number, tier?: string, games_played?: number, games_won?: number, progression_variant?: string }} props
 */
export function setAnalyticsUserProperties(props) {
    if (!ensureInitialized()) return;
    const stringProps = {};
    for (const [key, value] of Object.entries(props)) {
        stringProps[key] = value != null ? String(value) : null;
    }
    setUserProperties(analytics, stringProps);
}

function ensureRemoteConfigInitialized() {
    if (remoteConfig) return true;
    if (remoteConfigInitFailed) return false;

    try {
        const app = getApp();
        remoteConfig = getRemoteConfig(app);
        remoteConfig.settings.minimumFetchIntervalMillis = 3600000;
        return true;
    } catch (error) {
        remoteConfigInitFailed = true;
        console.warn("Remote Config unavailable:", error.message);
        return false;
    }
}

/**
 * Fetch progression config variant from Firebase Remote Config.
 * @returns {Promise<object|null>} Parsed progression config or null if unavailable
 */
export async function getProgressionConfigVariant() {
    if (!ensureRemoteConfigInitialized()) return null;

    try {
        await fetchAndActivate(remoteConfig);
        const configValue = getValue(remoteConfig, 'progression_config');
        if (!configValue || configValue.asString() === '') return null;
        return JSON.parse(configValue.asString());
    } catch (error) {
        console.warn("Remote Config fetch failed, falling back to default:", error.message);
        return null;
    }
}

/**
 * Get the active A/B test variant name for analytics tagging.
 * @returns {string} Variant name or 'default' if not set
 */
export function getConfigVariantName() {
    if (!ensureRemoteConfigInitialized()) return 'default';

    try {
        const variantValue = getValue(remoteConfig, 'progression_config_variant');
        const variant = variantValue ? variantValue.asString() : '';
        return variant || 'default';
    } catch (error) {
        return 'default';
    }
}
